import { api } from './client';

/**
 * ============================================================================
 * THE NOTIFICATION-PREFERENCE CLIENT — §18's matrix (F5, F6; Appendix L §2.10)
 * ============================================================================
 *
 *   GET  /pa/profile/notifications   — the rows, their channels, the answers, the amount
 *   POST /pa/profile/notifications   — a SPARSE write; the answer is the fresh matrix
 *
 * It mirrors the wire structs in `backend/internal/personalarea/notifications.go`, pinned
 * BOTH WAYS to `api/openapi.yaml` by `src/api/wire-spec.test.ts`.
 *
 * ============================================================================
 * ⚠ THE ONE RULE THIS FILE EXISTS FOR: **THE WRITE IS SPARSE, AND A POST FOR THAT REASON**
 * ============================================================================
 *
 * The staff route `PUT /users/{id}/notifications` decodes the whole preference struct and
 * writes all THIRTY-EIGHT `notif_*` columns from it. Any field the client forgets is absent
 * from the JSON, Go leaves it at its zero value and the UPDATE writes that zero over real
 * data — the D109 class this project names after `revokes_static_address`. The admin SPA is
 * safe only because `notifModel.ts` emits every key, and the cross-language guard that
 * proves it (`apps/admin/src/shared/api/fullReplacement.test.ts`) knows nothing about
 * `apps/portal`.
 *
 * So a portal screen posting that shape would be a SECOND client on a full-replacement body
 * with no guard, and one rendering only §18's seven ORIGINAL rows would silently zero the
 * six Amendment-01 families — **including `suspended`, the message that names the exact
 * figure restoring his service.** The backend was built sparse to make that impossible;
 * this client must not hand it a full matrix anyway.
 *
 * **`null` means «not changing». `false` means «switch it off».** A plain boolean cannot
 * express the difference, which is why the three channel fields are nullable and why
 * [buildNotificationsPatch] carries only what actually changed. `AlertsScreen.test.tsx`
 * asserts the recorded body, because a full-matrix POST is invisible to every rendering
 * test — the fetch double answers on the path and the screen re-renders from the response.
 *
 * ============================================================================
 * ⚠ WHAT THE ROWS ARE, AND WHY THEY ARE NOT A LIST IN THIS FILE
 * ============================================================================
 *
 * The row set and each row's channel set are DERIVED on the server from the `notif_*`
 * column list itself, so a fourteenth alert family appears the moment its columns exist,
 * and the one row with no SMS or messenger twin (`invoice` — «really a document-automation
 * subscription rather than a text alert») advertises exactly the channel it HAS. A screen
 * that hard-coded the matrix would render a checkbox the server drops, which is the
 * dead-control class one level down. The portal owns only the RUSSIAN LABEL per key
 * (`money/labels.ts`), because the operator has no editor for these names.
 *
 * `editable: false` + `locked_reason` is §18's monthly-invoice condition, enforced by the
 * server: the write answers `409 pa.notification_locked`, so the screen renders the STATE
 * and no control at all for such a row.
 */

/** The closed channel set, in §18's column order (`users.portalNotifyChannels`). */
export type PANotifyChannel = 'sms' | 'email' | 'messenger';

/** `PANotificationRow` — one row of §18's matrix. */
export interface PANotificationRow {
  /** The column family — «balance», «suspended», «invoice» … The portal owns the label. */
  key: string;
  /** The channels THIS ROW HAS, in §18's column order. A box outside this set is refused. */
  channels: PANotifyChannel[];
  /** The current answer per channel. Only the channels named in `channels` appear. */
  enabled: Record<string, boolean>;
  /** Marks the one row carrying a figure — the balance warning's threshold. */
  has_amount: boolean;
  /**
   * FALSE for a row this installation will not let him change (today only the monthly
   * invoice, and only when no live document template auto-generates for «subscribed
   * subscribers»). The screen then renders the state as TEXT: a control whose only
   * possible answer is `409 pa.notification_locked` is a dead control.
   */
  editable: boolean;
  /** A stable slug rather than a sentence, so the portal words it. "" when editable. */
  locked_reason: string;
}

/** `PANotifications` — the matrix and its one amount. */
export interface PANotifications {
  rows: PANotificationRow[];
  /** MINOR UNITS, like every money value on this surface. Zero is a real configured value. */
  balance_threshold: number;
}

/**
 * `PANotificationRowRequest` — ONE row of the sparse patch.
 *
 * ⚠ ALL THREE CHANNEL FIELDS ARE OPTIONAL AND NULLABLE, AND THAT IS THE WHOLE POINT: an
 * absent (or null) field is «not changing» and `false` is «switch it off».
 */
export interface PANotificationRowRequest {
  key: string;
  messenger?: boolean | null;
  sms?: boolean | null;
  email?: boolean | null;
}

/** `PANotificationsRequest` — the sparse write. Only the pairs named here are written. */
export interface PANotificationsRequest {
  rows?: PANotificationRowRequest[];
  /** MINOR UNITS; absent/null means «not changing». Negative is refused. */
  balance_threshold?: number | null;
}

/** The screen's own dirty set: row key → the channels it changed and their new answers. */
export type NotificationChanges = Record<string, Partial<Record<PANotifyChannel, boolean>>>;

/**
 * buildNotificationsPatch turns the screen's dirty set into the wire body.
 *
 * ⚠ IT IS THE ONE PLACE THE SPARSENESS IS PRODUCED, and it lives in the client rather than
 * in the component so that the property can be tested without a render — and so that a
 * later screen (a second matrix, a staff-facing one) cannot invent a second shape. A row
 * with no changed channel is not emitted at all; a threshold that did not change is not
 * emitted at all; and NOTHING is ever emitted for a channel the subscriber did not touch.
 */
export function buildNotificationsPatch(
  changes: NotificationChanges,
  threshold: number | null,
): PANotificationsRequest {
  const rows: PANotificationRowRequest[] = [];
  for (const [key, channels] of Object.entries(changes)) {
    const entries = Object.entries(channels) as Array<[PANotifyChannel, boolean]>;
    if (entries.length === 0) continue;
    const row: PANotificationRowRequest = { key };
    for (const [channel, value] of entries) {
      row[channel] = value;
    }
    rows.push(row);
  }
  const body: PANotificationsRequest = {};
  if (rows.length > 0) body.rows = rows;
  if (threshold !== null) body.balance_threshold = threshold;
  return body;
}

/** patchIsEmpty reports a body that would write nothing — the screen then sends none. */
export function patchIsEmpty(body: PANotificationsRequest): boolean {
  return (body.rows === undefined || body.rows.length === 0) && body.balance_threshold == null;
}

export function getNotifications(): Promise<PANotifications> {
  return api.get<PANotifications>('/pa/profile/notifications');
}

/**
 * setNotifications writes the sparse patch and returns the FRESH matrix.
 *
 * The response is what the screen renders — never its own optimistic copy. The server
 * refuses the WHOLE request rather than applying part of it (`pa.notification_row_unknown`,
 * `pa.notification_channel_unknown`, `pa.notification_locked`, `pa.invalid_threshold`), so
 * after a refusal the screen's own boxes are the only wrong thing on the page and re-reading
 * the server's answer is what corrects them.
 */
export function setNotifications(body: PANotificationsRequest): Promise<PANotifications> {
  return api.post<PANotifications>('/pa/profile/notifications', body);
}
